import moment from "moment";

/**
 * Time Validation and Parsing Functions
 * Utility functions for parsing and validating time inputs
 */

/**
 * Parse time input string into formatted time
 * @param {string} input - Time input string
 * @returns {string|false} Formatted time string or false if invalid
 */
export const parseTimeInput = (input) => {
  input = input.trim().toLowerCase();
  let timeRegex = /^(\d{1,2}):?(\d{2})?\s*([ap]m?)?$/;
  let match = input.match(timeRegex);

  if (!match) {
    timeRegex = /^(\d{1,2}):?(\d{1,2})?\s*([ap]m?)?$/;
    match = input.match(timeRegex);
    if (!match) return false;
  }

  let [_, hour, minutes, period] = match;
  hour = hour.padStart(2, "0");
  minutes = minutes ? minutes.padStart(2, "0") : "00";
  if (
    parseInt(hour, 10) > 12 ||
    parseInt(hour, 10) < 1 ||
    parseInt(minutes, 10) > 59
  ) {
    return false;
  }
  period = period
    ? period?.toUpperCase()
    : parseInt(hour, 10) < 12
      ? "AM"
      : "PM";

  return `${hour}:${minutes} ${period}`;
};

/**
 * Format time string using moment.js
 * @param {string} time - Time string to format
 * @returns {string|false} Formatted time or false if invalid
 */
export const timeFormatChange = (time) => {
  const input = parseTimeInput(time);
  if (input) {
    const momentTime = moment(input, "hh:mm A");
    if (!momentTime.isValid()) return false;
    return momentTime.format("hh:mm A");
  }
  return false;
};

/**
 * Format time value to two-digit string
 * @param {number|string} time - Time value
 * @returns {string} Formatted time string
 */
export const formatTime = (time) => {
  return time.toString().padStart(2, "0");
};

/**
 * Validate time format (HH:mm:ss)
 * @param {string} timeString - Time string to validate
 * @returns {boolean} True if valid format
 */
export const validateTimeFormat = (timeString) => {
  const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
  return timeRegex.test(timeString);
};

/**
 * Get time components from time string
 * @param {string} timeString - Time string in HH:mm:ss format
 * @returns {Object} Object with hours, minutes, seconds
 */
export const getTimeComponents = (timeString) => {
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return { hours, minutes, seconds };
};

/**
 * Convert date and time string to timestamp
 * @param {Date} dateEdit - Date object
 * @param {string} timeString - Time string
 * @returns {string} Formatted timestamp
 */
export const convertTimeStamp = (dateEdit, timeString) => {
  const date = new Date(dateEdit);
  const datePart = date.toLocaleDateString("en-US");
  const timePart = timeString;
  const combinedDateTimeString = `${datePart} ${timePart}`;
  return moment(combinedDateTimeString).format();
};
